import StripeForm from "./StripeForm";
import { useRef, useState, useEffect } from "react";
import { Button, SlideOutModal, SlideOutModalProps, SpinnerDefault } from "../../../components";
import { sharedProperty } from "../../../hooks";
import { PaymentProcessorHandler } from "../interface";
import { InvoiceModel, BookingModel } from "../../../data/entities";
import { clearBookingStorage, formatPrice, trackBookingPurchase, trackPaymentFailed } from "../../../utils";
import { useNavigate } from "react-router-dom";
import { routes } from "../../../app/routes";
import BookingService from "../../../services/booking/BookingService";
import MonerisForm from "./MonerisForm";
import { PaymentGatewayType } from "../../../models";
import { usePaymentModalCalculations } from "../hooks";
import { PaymentErrorModal } from "./PaymentErrorModal";

export interface AddPaymentModalProps extends SlideOutModalProps {
  showState: sharedProperty<boolean>;
  paymentGatewayType: PaymentGatewayType;
  invoice: InvoiceModel;
  booking: BookingModel;
  locationCode: string;
}

export function PaymentModal(props: Readonly<AddPaymentModalProps>) {

  const [paymentStatus, setPaymentStatus] = useState<string | null>(null);
  const [paymentError, setPaymentError] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const paymentFormRef = useRef<PaymentProcessorHandler>(null);
  const navigate = useNavigate();
  const bookingService = new BookingService();

  const {
    totalCharges,
    totalHolds,
    hasCharges,
    hasHolds,
  } = usePaymentModalCalculations(props.invoice);

  useEffect(() => {
    if(paymentStatus === "succeeded"){
      (async () => {
        await bookingService.markBookingAsCompletedAsync(props.booking.id);
        trackBookingPurchase(props.booking, props.invoice);
        clearBookingStorage();
        navigate(
          routes.book.confirmation.replace(":locationCode", props.locationCode), 
          { 
            state:{
            booking: props.booking, 
            invoice: props.invoice,
            }
          });
      })();
    }
    if(paymentStatus === "failed"){
      trackPaymentFailed(props.booking, props.invoice);
      setIsProcessing(false);
      setPaymentError("Payment failed. Please check your payment details and try again.");
    }
  }, [navigate, paymentStatus, props.booking])

  const handleSubmit = (event: any) => {
    if (paymentFormRef.current) {
      setPaymentError(null);
      setIsProcessing(true);
      paymentFormRef.current.handleSubmit(event);
    }
  };

  const handleRetry = () => {
    setPaymentStatus(null);
    setPaymentError(null);
    setIsProcessing(false);
  };

  const handleCancel = () => {
    setPaymentStatus(null);
    setPaymentError(null);
    setIsProcessing(false);
    props.showState.setter(false);
  };

  const renderPaymentForm = () => {
    switch(props.paymentGatewayType){
      case PaymentGatewayType.Stripe:
        return <StripeForm booking={props.booking} invoice={props.invoice} setPaymentStatus={setPaymentStatus} ref={paymentFormRef}/>
      case PaymentGatewayType.Moneris:
        return <MonerisForm booking={props.booking} invoice={props.invoice} setPaymentStatus={setPaymentStatus} ref={paymentFormRef}/>
      default:
        return <MonerisForm booking={props.booking} invoice={props.invoice} setPaymentStatus={setPaymentStatus} ref={paymentFormRef}/>
    }
  }

  
  if (paymentError) {
    return (
      <PaymentErrorModal
        {...props}
        error={paymentError}
        amount={props.invoice.total}
        onRetry={handleRetry}
        onCancel={handleCancel}
      />
    );
  }

  return (
    <SlideOutModal
      {...props}
      title="Complete Payment"
      footer={
        <div className="container p-0">
          <div className="row g-0">
            <div className="col-12 pe-2">
              <Button
                onClick={handleCancel}
                variant="cancel"
                className="w-100"
                disabled={isProcessing}
              >
                Cancel
              </Button>
            </div>
            <div className="col-12 ps-2">
              <Button
                onClick={handleSubmit}
                variant="primary"
                className="w-100"
                disabled={isProcessing}
              >
                {isProcessing ? "Processing..." : `Pay`}
              </Button>
            </div>
          </div>
        </div>
      }
    >
      {/* Loading */}
      {isProcessing && (
        <>
          <div 
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 9998,
              overflow: 'hidden'
            }}
          />
          <SpinnerDefault
            overlay={true}
            message={"Processing payment..."}
            submessage={"Please do not close this window"}
            large={true}
          />
        </>
      )}

      {/* Payment Form */}
      <div className="mb-4">
        {renderPaymentForm()}
      </div>

      {/* Total Due Now */}
      {hasCharges && (
        <div className="d-flex justify-content-between align-items-center p-3 mb-3 rounded bg-primary-pastel">
          <div className="d-flex align-items-center">
            <i className="fa fa-credit-card me-2 text-primary-pastel-bold"></i>
            <span className="fw-semibold">Total Due Now</span>
          </div>
          <span className="fw-bold">{formatPrice(totalCharges)}</span>
        </div>
      )}

      {/* Total On Hold */}
      {hasHolds && (
        <div className="d-flex justify-content-between align-items-center p-3 mb-3 rounded bg-secondary-pastel">
          <div className="d-flex align-items-center">
            <i className="fa fa-lock me-2 text-secondary-pastel-bold"></i>
            <span className="fw-semibold">Total On Hold</span>
          </div>
          <span className="fw-bold">{formatPrice(totalHolds)}</span>
        </div>
      )}
    </SlideOutModal>
  );
}

export default PaymentModal;